/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Module-level handle for the credential sign-in attempt in flight, so the
 * "Sign In Again" button on the connection-error overlay can end it.
 *
 * The screens pass the thunk an abort signal that lives as long as the screen
 * does, so aborting it would also poison every later attempt from that screen.
 * Each attempt instead runs on its own session signal, linked to the screen's:
 * the screen unmounting still ends it, and {@link cancelSignInSession} ends
 * just this one attempt. The CRITICAL loop's backoff wait rejects on that
 * signal, which unwinds the thunk back to the form.
 *
 * One handle is enough: only one interactive sign-in runs at a time, and a new
 * session replaces (and ends) any previous one.
 */
let sessionController: AbortController | null = null;

/**
 * Starts a session for a new sign-in attempt and returns its signal. Aborted
 * when `parentSignal` aborts, when {@link cancelSignInSession} is called, or
 * when a later session starts.
 */
export function beginSignInSession(parentSignal: AbortSignal): AbortSignal {
  if (sessionController !== null) {
    sessionController.abort();
  }
  const controller: AbortController = new AbortController();
  sessionController = controller;
  if (parentSignal.aborted) {
    controller.abort();
  } else {
    parentSignal.addEventListener(
      "abort",
      (): void => {
        controller.abort();
      },
      { once: true }
    );
  }
  return controller.signal;
}

/**
 * Releases the handle once an attempt settles. Ignores a signal that is no
 * longer the current session, so a late finish cannot drop a newer one.
 */
export function endSignInSession(signal: AbortSignal): void {
  if (sessionController !== null && sessionController.signal === signal) {
    sessionController = null;
  }
}

/** Ends the sign-in attempt in flight, if any. Safe to call when idle. */
export function cancelSignInSession(): void {
  if (sessionController !== null) {
    sessionController.abort();
    sessionController = null;
  }
}
